import React, { useState, useEffect, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import ChatMessage from './ChatMessage';
import FilterDropdown from './FilterDropdown';
import RaidFinder from './RaidFinder';
import './ChatInterface.css';

const client = generateClient();

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [autoScrollPaused, setAutoScrollPaused] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([6, 18]); // Default to channels 6 and 18
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [websocket, setWebsocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [showRaidFinder, setShowRaidFinder] = useState(true);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const keepAliveIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const hasInitiallyLoadedRef = useRef(false);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle manual scrolling with infinite scroll
  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setIsAutoScroll(isAtBottom);
    }
  };

  // Filter messages based on selected channels
  const filterMessages = (messagesToFilter) => {
    return messagesToFilter.filter(message => selectedChannels.includes(message.channel));
  };

  // Handle channel filter toggle
  const handleChannelToggle = (channelId) => {
    setSelectedChannels(prev => {
      if (prev.includes(channelId)) {
        return prev.filter(id => id !== channelId);
      } else {
        return [...prev, channelId];
      }
    });
  };

  // Handle raid finder toggle
  const handleToggleRaidFinder = () => {
    setShowRaidFinder(prev => !prev);
  };

  // WebSocket connection setup
  const setupWebSocket = () => {
    // Clear any existing timeouts/intervals
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Close existing connection if any
    if (websocket && websocket.readyState !== WebSocket.CLOSED) {
      websocket.close();
    }
    
    // Replace with your actual WebSocket endpoint from AWS API Gateway
    const wsUrl = process.env.REACT_APP_WEBSOCKET_URL || 'wss://j97ah6qnpg.execute-api.us-east-2.amazonaws.com/production';
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      setConnectionStatus('connected');
      
      // Reset reconnection attempts on successful connection
      localStorage.removeItem('wsReconnectAttempts');
      
      // Subscribe to channels
      selectedChannels.forEach(channel => {
        const subscribeMessage = {
          action: 'subscribe',
          channel: channel
        };
        ws.send(JSON.stringify(subscribeMessage));
      });
      
      // Start keepalive mechanism - send ping every 5 minutes to prevent idle timeout
      keepAliveIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping', timestamp: Date.now() }));
        } else {
          if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
          }
        }
      }, 5 * 60 * 1000); // 5 minutes
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'newMessage') {
          // Check if this is actually a raid update wrapped in newMessage
          if (data.message && data.message.type === 'raidUpdate') {
            // This is raid data, let RaidFinder handle it
            return;
          }
          
          const newMessage = data.message;
          
          setAllMessages(prev => {
            const existingIds = new Set(prev.map(msg => msg.messageId));
            if (existingIds.has(newMessage.messageId)) {
              return prev;
            }
            
            const combined = [...prev, newMessage].sort((a, b) => a.timestamp - b.timestamp);
            return combined;
          });
        } else if (data.type === 'raidUpdate') {
          // Raid updates are handled by RaidFinder component
          return;
        } else {
        }
      } catch (error) {
      }
    };
    
    ws.onclose = (event) => {
      setConnectionStatus('disconnected');
      
      // Clear keepalive interval
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
      
      // Don't reconnect if it was a normal closure (code 1000) or if user is closing the page
      if (event.code === 1000 || event.code === 1001) {
        return;
      }
      
      // Exponential backoff reconnection: 1s, 2s, 4s, 8s, 16s, then 30s max
      const reconnectAttempts = parseInt(localStorage.getItem('wsReconnectAttempts') || '0');
      const maxAttempts = 10;
      
      if (reconnectAttempts < maxAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
        
        localStorage.setItem('wsReconnectAttempts', (reconnectAttempts + 1).toString());
        
        reconnectTimeoutRef.current = setTimeout(() => {
          setupWebSocket();
        }, delay);
      } else {
        setConnectionStatus('failed');
        localStorage.removeItem('wsReconnectAttempts');
      }
    };
    
    ws.onerror = (error) => {
      setConnectionStatus('error');
    };
    
    setWebsocket(ws);
  };

  // Update WebSocket subscriptions when channels change
  const updateWebSocketSubscriptions = () => {
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      // Unsubscribe from all channels first
      websocket.send(JSON.stringify({
        action: 'unsubscribeAll'
      }));
      
      // Subscribe to new channels
      selectedChannels.forEach(channel => {
        websocket.send(JSON.stringify({
          action: 'subscribe',
          channel: channel
        }));
      });
    }
  };

  // Load more messages (older messages) - DISABLED
  const loadMoreMessages = async () => {
    return;
  };

  // Fetch messages from DynamoDB (initial load only)
  const fetchMessages = async () => {
    try {
      setLoading(true);
      
      const channelQueries = selectedChannels.map(channel => 
        client.graphql({
          query: `
            query ListMessagesByChannel($channel: Int!) {
              listMessagesByChannel(
                channel: $channel
                limit: 50
                sortDirection: "DESC"
              ) {
                items {
                  messageId
                  timestamp
                  channel
                  playerName
                  message
                  linkTypes
                  linkColors
                }
                nextToken
              }
            }
          `,
          variables: {
            channel: channel
          }
        })
      );

      const responses = await Promise.all(channelQueries);
      
      const hasErrors = responses.some(response => response.errors && response.errors.length > 0);
      if (hasErrors) {
        const errors = responses.flatMap(response => response.errors || []);
        throw new Error(`GraphQL errors: ${errors.map(e => e.message).join(', ')}`);
      }
      
      const allMessages = responses.flatMap(response => 
        response.data?.listMessagesByChannel?.items || []
      );
      
      // Sort messages by timestamp (oldest first for display)
      const sortedAllMessages = allMessages.sort((a, b) => a.timestamp - b.timestamp);
      
      // Set all messages for initial load
      setAllMessages(sortedAllMessages);
      setHasMoreMessages(false);
      setLastFetchTime(new Date().toISOString());
      setError(null);
      
    } catch (err) {
      setError(`Failed to load messages: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Set up initial data fetch
  useEffect(() => {
    fetchMessages();
  }, []);

  // Update WebSocket subscriptions when channels change (without refreshing messages)
  useEffect(() => {
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      updateWebSocketSubscriptions();
    }
  }, [selectedChannels]);

  // Set up WebSocket connection
  useEffect(() => {
    setupWebSocket();

    // Cleanup on unmount
    return () => {
      // Clear intervals and timeouts
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Close WebSocket connection
      if (websocket && websocket.readyState !== WebSocket.CLOSED) {
        websocket.close(1000, 'Component unmounting');
      }
      
      // Reset reconnection attempts
      localStorage.removeItem('wsReconnectAttempts');
    };
  }, []);

  // Apply filtering when selectedChannels or allMessages change
  useEffect(() => {
    if (allMessages.length > 0) {
      const filteredMessages = filterMessages(allMessages);
      setMessages(filteredMessages);
    }
  }, [selectedChannels, allMessages]);

  // Auto-scroll when messages change (but not when loading more or paused)
  useEffect(() => {
    if (!autoScrollPaused && !loadingMore) {
      scrollToBottom();
    }
  }, [messages, autoScrollPaused, loadingMore]);

  // Force scroll to bottom on initial load only
  useEffect(() => {
    if (!loading && messages.length > 0 && !loadingMore && !hasInitiallyLoadedRef.current && !autoScrollPaused) {
      hasInitiallyLoadedRef.current = true;
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [loading, loadingMore, autoScrollPaused]);

  if (loading) {
    return (
      <div className="chat-interface">
        <div className="loading">
          <div>Loading chat messages...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-interface">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <FilterDropdown
          selectedChannels={selectedChannels}
          onChannelToggle={handleChannelToggle}
          showRaidFinder={showRaidFinder}
          onToggleRaidFinder={handleToggleRaidFinder}
          autoScrollPaused={autoScrollPaused}
          onToggleAutoScroll={() => setAutoScrollPaused(!autoScrollPaused)}
        />
        <div className="chat-stats">
          <span>Messages: {messages.length}</span>
          <span style={{ 
            fontSize: '0.7rem', 
            opacity: 0.7, 
            marginLeft: '10px',
            color: connectionStatus === 'connected' ? '#4CAF50' : '#f44336'
          }}>
            WebSocket: {connectionStatus}
          </span>
          {lastFetchTime && (
            <span className="last-fetch-time" style={{ fontSize: '0.7rem', opacity: 0.7, marginLeft: '10px' }}>
              Last: {new Date(lastFetchTime).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
      
      <div className="main-content">
        <div 
          className="chat-container"
          ref={chatContainerRef}
          onScroll={handleScroll}
        >
          <div className="messages-list">
            {messages.map((message) => (
              <ChatMessage key={message.messageId} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
        
        {showRaidFinder && (
          <div className="raid-finder-panel">
            <RaidFinder 
              websocket={websocket}
              connectionStatus={connectionStatus}
            />
          </div>
        )}
      </div>
      
      <div className="chat-info">
        <button 
          className="refresh-button"
          onClick={() => fetchMessages()}
          style={{ padding: '5px 10px' }}
        >
          Refresh
        </button>
        {!isAutoScroll && (
          <button 
            className="scroll-to-bottom"
            onClick={() => {
              setIsAutoScroll(true);
              scrollToBottom();
            }}
          >
            New Messages
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;